import { useId, useMemo } from 'react';
import { Controller, Control, FieldPath, FieldValues } from 'react-hook-form';
import { InputField } from '@/components/InputField';

type RoleLabelFieldProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> = {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  placeholder: string;
  noOptionsMessage?: string;
  /** Lista de posições cadastrada na categoria (roster_group.position_order). Vazia/undefined = sem sugestões. */
  positionOrder?: string[] | null;
  error?: string;
};

/**
 * role_label é texto livre no schema — mas quando a categoria selecionada já tem
 * position_order cadastrado, sugerimos essas posições via <datalist> nativo (reduz
 * erro de digitação sem travar cadastros antigos nem exigir curar a lista antes).
 *
 * Usa <input list> + <datalist> em vez de um combobox custom (chakra-react-select):
 * o dropdown de sugestões é renderizado pelo próprio navegador — sem CSS/z-index/
 * portal/overflow pra gerenciar, sempre no lugar e tamanho certos dentro de um Modal.
 */
export function RoleLabelField<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>>({
  control,
  name,
  label,
  placeholder,
  positionOrder,
  error,
}: RoleLabelFieldProps<TFieldValues, TName>) {
  const datalistId = useId();
  const options = useMemo(() => positionOrder ?? [], [positionOrder]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <>
          <InputField
            withoutFloating
            label={label}
            placeholder={options.length > 0 ? placeholder : undefined}
            maxLength={50}
            list={options.length > 0 ? datalistId : undefined}
            rootProps={{ isInvalid: !!error, errorMessage: error }}
            {...field}
          />
          {options.length > 0 ? (
            <datalist id={datalistId}>
              {options.map(option => (
                <option key={option} value={option} />
              ))}
            </datalist>
          ) : null}
        </>
      )}
    />
  );
}
